import { FieldPath } from 'react-hook-form';
import { TabbedContentProps, useTabbedContent } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { useDialogForm, UseDialogFormProps } from '../dialogForm/useDialogForm';
import { TabbedDialogFormProps, WithTabs } from './types';

interface UseTabbedDialogFormProps<T extends string, F extends object>
    extends WithTabs<T>,
        Pick<TabbedContentProps<T>, 'tabLabels' | 'tabProps'>,
        Omit<UseDialogFormProps, 'renderForm' | 'renderPrimaryAction'> {
    tabFields: Record<T, Array<FieldPath<F>>>;
    renderForm: (props: TabbedDialogFormProps<T>) => ReturnType<UseDialogFormProps['renderForm']>;
}

export const useTabbedDialogForm = <T extends string, F extends object>({
    tabs,
    disabledTabs,
    initialTab,
    tabLabels,
    tabFields,
    tabProps,
    onClose,
    renderForm,
    ...dialogFormProps
}: UseTabbedDialogFormProps<T, F>) => {
    const { Tabs, state } = useTabbedContent({ tabs, initialTab, disabledTabs });

    return useDialogForm({
        ...dialogFormProps,
        onClose: () => {
            onClose?.();
            state.setCurrentTab(tabs[0]);
        },
        renderForm: (renderFormProps) => (
            <Stack spacing="400" vertical fill>
                <Tabs
                    tabLabels={tabLabels}
                    tabProps={tabProps}
                    onTabClick={async (_, value) => {
                        const validate = renderFormProps.formRef?.current?.validate;
                        if (validate) {
                            const result = await validate(tabFields[state.currentTab]);
                            if (result) {
                                state.setCurrentTab(value as T);
                            }
                        }
                    }}
                    sticky
                />
                {renderForm({ tabs, ...renderFormProps, ...state })}
            </Stack>
        ),
    });
};
